import { Platform, PlatformModule } from '@angular/cdk/platform';
import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { delay, filter, first, map, take, takeUntil } from 'rxjs/operators';

import { ToolsHelper } from '@app/core/helpers/tools.helper';
import {
  BtrEventModel,
  BtrPassportModel,
  ClusterModel,
  ProfileModel,
  UserModel,
} from '@app/core/models';
import { ISettingsApiModel } from '@app/core/models/api';
import { BtrStaticPageModel } from '@app/core/models/btr-static-page.model';
import {
  APP_CONFIG,
  EventsService,
  IAppConfig,
  StorageService,
  UserService,
} from '@app/core/services';
import { AuthComponentModule } from '@app/shared/components/auth/auth.component.module';
import { MaterialIconsModule } from '@app/shared/modules/material-icons.module';
import {
  BtrEventApiService,
  BtrPassportApiService,
  DealApiService,
  GuideApiService,
} from '@app/shared/services/api';
import { BtrStaticPageApiService } from '@app/shared/services/api/btr-static-page.api.service';

interface ICounts {
  guides: number;
  events: number;
  passports: number;
  deals: number;
  yTickets: number;
  yPassports: number;
  yDeals: number;
  savedPlaces: number;
}

@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    MaterialIconsModule,
    AuthComponentModule,
    PlatformModule,
  ],
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  providers: [
    DealApiService,
    BtrEventApiService,
    GuideApiService,
    BtrStaticPageApiService,
    BtrPassportApiService,
  ],
})
export class SidenavComponent implements OnInit, OnDestroy {
  @HostBinding('style.background-color') public backgroundColor!: string;
  public showMobileMenu = false;
  public readonly ToolsHelper = ToolsHelper;
  public readonly nowDate = new Date().toISOString();
  public cluster: ClusterModel;
  public clusterConfig: ISettingsApiModel;
  public counts: ICounts = {
    guides: 0,
    events: 0,
    passports: 0,
    deals: 0,
    yTickets: 0,
    yPassports: 0,
    yDeals: 0,
    savedPlaces: 0,
  };
  public showMapValue = false;
  public showMobileMapButton = true;
  time = Date.now();
  staticPages: Array<BtrStaticPageModel> = [];

  get user(): UserModel {
    return this.storageService.USER;
  }

  get savedPlaces(): number {
    return this.storageService?.BOOKMARK_IDS?.length ?? 0;
  }

  private destroy$: Subject<void> = new Subject();
  private events: Array<BtrEventModel | BtrPassportModel>;

  constructor(
    public platform: Platform,
    @Inject(APP_CONFIG) public readonly appConfig: IAppConfig,
    private readonly storageService: StorageService,
    private readonly btrEventApiService: BtrEventApiService,
    private readonly guideApiService: GuideApiService,
    private readonly eventsService: EventsService,
    private readonly userService: UserService,
    private readonly btrStaticPageApiService: BtrStaticPageApiService,
  ) {}

  ngOnInit(): void {
    this.eventsService.openMobileMap$
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        this.showMapValue = val;
      });
    this.eventsService.showMobileMapButton$
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        this.showMobileMapButton = val;
      });
    this.btrStaticPageApiService
      .getStaticPages()
      .pipe(take(1))
      .subscribe(val => {
        this.staticPages = val;
      });
    this.eventsService.ticketPurchaseComplete$
      .pipe(
        filter(val => val),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.initUserCounters();
        this.eventsService.ticketPurchaseCompleteSet(false);
      });
    this.backgroundColor = this.appConfig.settings.header_color
      ? this.appConfig.settings.header_color
      : '#ffffff';
    if (this.platform.isBrowser) {
      this.updateProfile();
      this.cluster = this.storageService.SELECTED_CLUSTER;
      this.clusterConfig = this.storageService.CONFIG;
      if (this.cluster?.id) {
        this.initCounters();
      } else {
        this.eventsService.setUserDone$
          .pipe(delay(100), takeUntil(this.destroy$))
          .subscribe(value => {
            if (value) {
              this.updateProfile();
              this.initUserCounters();
            }
          });
      }

      this.eventsService.sendToTripSource$
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          if (value && value.id) {
            const bookmark_ids = this.storageService.BOOKMARK_IDS;

            if (bookmark_ids && bookmark_ids.includes(value.id)) {
              const user = this.storageService.USER;
              if (user) {
                this.storageService.PLACE_IDS =
                  this.storageService.PLACE_IDS.filter(
                    item => item !== value.id,
                  );
                this.userService.removeBookmark(value.id).subscribe(
                  () => {
                    this.updateProfile();
                  },
                  () => {
                    this.storageService.PLACE_IDS.push(value.id);
                  },
                );
              }
            } else {
              const user = this.storageService.USER;
              if (user) {
                this.userService.addBookmark(value.id).subscribe(() => {
                  this.updateProfile();
                });
              } else {
                this.eventsService.setOpenLogin(true);
              }
            }
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  toggleMobileMenu(event: MouseEvent): void {
    event.preventDefault();
    this.showMobileMenu = !this.showMobileMenu;
  }

  hideMobileMenu(): void {
    this.showMobileMenu = false;
  }

  showMap(): void {
    this.showMapValue = !this.showMapValue;
    this.eventsService.openMobileMap$.next(this.showMapValue);
    this.eventsService.setRouteMapState(!this.showMapValue);
  }

  returnClearLink(url: string): string {
    return url.match(
      // eslint-disable-next-line unicorn/better-regex
      /^(?:https?:\/\/)?(?:[^@\/\n]+@)?(?:www\.)?([^:\/\n]+)/im,
    )[1];
  }

  private initCounters(): void {
    this.btrEventApiService
      .getEvents({
        cluster: this.storageService.CONFIG.cluster,
        sort: 'start',
        sort_order: 'ASC',
        output_fields: ['beacons', 'tickets'].join(','),
        filters: `actual,expired`,
      })
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe(items => {
        let passports: Array<BtrPassportModel> = [];
        let events: Array<BtrEventModel> = [];
        for (const item of items) {
          if (item.isPassport) {
            passports.push(item as any);
          } else {
            events.push(item);
          }
        }
        this.events = items;
        this.counts.passports = passports.filter(
          passport => passport.showInWhiteLabel && !passport.isExpired,
        )?.length;
        this.counts.events = events.filter(
          event => event.showInWhiteLabel && !event.isExpired,
        )?.length;
        this.storageService.EVENTS =
          events.filter(event => event.showInWhiteLabel && !event.isExpired) ??
          [];
        this.eventsService.setUserDone$
          .pipe(delay(100), takeUntil(this.destroy$))
          .subscribe(value => {
            if (value) {
              this.updateProfile();
              this.initUserCounters();
            }
          });
        if (this.storageService.USER) {
          this.initUserCounters();
        }
      });
    this.guideApiService
      .getGuidesByCluster(this.cluster.id)
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe(response => {
        this.counts.guides = response.length;
      });
  }

  private initUserCounters(): void {
    const profile: ProfileModel = this.storageService.PROFILE;
    let couponCount = 0;
    let ticketCount = 0;
    let passportCount = 0;
    if (this.storageService?.CONFIG?.cluster) {
      if (this.events?.length) {
        const currentEvent = this.storageService.EVENT;
        if (!!currentEvent) {
          const freshDataEvent = this.events.find(
            item => item.id === currentEvent.id,
          );
          if (freshDataEvent) {
            this.storageService.EVENT = freshDataEvent;
          }
        }
        this.counts.yTickets = 0;
        this.counts.yPassports = 0;
        const tickets = profile.tickets;
        const eventsIds = [
          ...new Set(profile.tickets.map(ticket => ticket.event)),
        ];
        eventsIds.map(eventId => {
          const neededEvent = this.events.find(event => event.id === eventId);
          if (!neededEvent) {
            return;
          }
          const neededTickets = tickets.filter(
            ticket => ticket.event === eventId && !ticket.bundle?.length,
          );
          for (const ticket of neededTickets) {
            if (ticket.isPassport) {
              passportCount += ticket.statuses.length;
            } else {
              ticketCount += ticket.statuses.length;
            }
          }
          this.counts.yPassports = passportCount;
          this.counts.yTickets = ticketCount;
        });
      } else {
        this.btrEventApiService
          .getEvents({
            output_fields: ['beacons', 'tickets'].join(','),
            filters: `actual,expired,paid,free`,
            cluster: this.storageService.CONFIG.cluster,
          })
          .pipe(
            map(events => {
              {
                const currentEvent = this.storageService.EVENT;
                if (!!currentEvent) {
                  const freshDataEvent = events.find(
                    item => item.id === currentEvent.id,
                  );
                  if (freshDataEvent) {
                    this.storageService.EVENT = freshDataEvent;
                  }
                }
                this.counts.yTickets = 0;
                this.counts.yPassports = 0;
                const tickets = profile.tickets;
                const eventsIds = [
                  ...new Set(profile.tickets.map(ticket => ticket.event)),
                ];
                eventsIds.map(eventId => {
                  const neededEvent = events.find(
                    event => event.id === eventId,
                  );
                  if (!neededEvent) {
                    return;
                  }
                  const neededTickets = tickets.filter(
                    ticket =>
                      ticket.event === eventId && !ticket.bundle?.length,
                  );
                  for (const ticket of neededTickets) {
                    if (ticket.isPassport) {
                      passportCount += ticket.statuses.length;
                    } else {
                      ticketCount += ticket.statuses.length;
                    }
                  }
                  this.counts.yPassports = passportCount;
                  this.counts.yTickets = ticketCount;
                });
              }
            }),
            take(1),
          )
          .subscribe();
      }
    }
    this.counts.yDeals = couponCount;
    let countSavedPlaces = 0;
    for (const bookmark of profile.bookmarks) {
      if (!bookmark.isDeleted) {
        ++countSavedPlaces;
      }
    }
    this.counts.savedPlaces = countSavedPlaces;
  }

  private updateProfile(): void {
    if (this.storageService.USER) {
      let clusterId = null;

      if (this.appConfig.settings.is_white_label) {
        const cluster = this.storageService.SELECTED_CLUSTER;
        clusterId = cluster ? cluster.id : null;
      }

      this.userService.getBookmarks(clusterId).subscribe(response => {
        const ids = [];
        response.map(item => ids.push(item.id));
        this.storageService.BOOKMARK_IDS = ids;
        this.storageService.PLACE_IDS = ids;
        this.counts.savedPlaces = ids.length ?? 0;
      });
    }
  }
}
